import React from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Checkbox,
    TextareaAutosize,
} from "@mui/material";
import { Autocomplete } from "@mui/material";
import {
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    Tooltip,
    Typography,
    ListSubheader,
    Divider,
    ListItemIcon,
    ListItemText,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import useSetParamToURL from "../../hooks/useSetParamToURL";

const validationSchema = Yup.object({
    selectedUsers: Yup.array().min(1, "Select at least one user"),
    sender: Yup.string().required("Sender is required"),
    // Subject and message are optional for now – backend will fall back to defaults if empty
    subject: Yup.string(),
    message: Yup.string(),
});

const maskEmail = (email) => {
    if (!email || !email.includes("@")) return email || "";
    const [local, domain] = email.split("@");
    if (local.length <= 3) return `${local.charAt(0)}***@${domain}`;
    return `${local.substring(0, 2)}***${local.slice(-1)}@${domain}`;
};

const ShareNotesDialog = ({
    handleClose,
    handleSubmit,
    users,
    noteId,
    noteText = "",
    availableIntegrations = {},
    noteCutoff = 13,
    noteMaxWidth = 130,
    isSubmitting = false,
    accountId,
    facilityId,
    redirectAfter }) => {
    const uniqueUsers = React.useMemo(() => {
        if (!Array.isArray(users)) return [];

        const seenKeys = new Set();
        const result = [];

        users.forEach((user, index) => {
            if (!user) return;

            // Strip all non-printable/hidden characters and normalize
            const normalize = (val) => String(val || "").trim().toLowerCase().replace(/[^\x20-\x7E]/g, "");

            const email = normalize(user.email);
            const fullName = normalize(user.fullName);
            const id = normalize(user._id || user.id);

            // Compound key to catch almost every variation
            // Priority: Email, then Name+ID, then Name+Index as last resort
            let key = email ? `email:${email}` : (fullName ? `name:${fullName}:${id}` : `id:${id}`);

            if (!key || key.endsWith(":")) {
                key = `index:${index}`;
            }

            if (seenKeys.has(key)) {
                return;
            }

            seenKeys.add(key);
            result.push(user);
        });

        return result;
    }, [users]);
    const showTooltip = noteText && noteText.length > noteCutoff;
    const computedSenderOptions = [
        {
            id: "microsoft",
            label: availableIntegrations.microsoft?.connected
                ? `Microsoft 365 (${availableIntegrations.microsoft.email})`
                : "Microsoft 365",
        },
        {
            id: "google",
            label: availableIntegrations.google?.connected
                ? `Google (${availableIntegrations.google.email})`
                : "Google",
        },
    ];

    const hasConnectedEmail = availableIntegrations.microsoft?.connected || availableIntegrations.google?.connected;
    const { setPathWithParams } = useSetParamToURL();

    const isReportShare = !noteId;

    const defaultSubject = React.useMemo(() => {
        if (isReportShare) {
            const raw = noteText || "";
            const title = raw.replace(/^Report:\s*/i, "") || "Generated Report";
            return `SimpleSNF Report: ${title}`;
        }

        const base = noteText || "";
        if (!base) return "SimpleSNF Note";
        const trimmed = base.length > 80 ? `${base.slice(0, 77)}...` : base;
        return `SimpleSNF Note: ${trimmed}`;
    }, [isReportShare, noteText]);

    const defaultMessage = React.useMemo(() => {
        if (isReportShare) {
            const raw = noteText || "";
            const title = raw.replace(/^Report:\s*/i, "") || "this report";
            return `I've shared the report "${title}" with you from SimpleSNF.`;
        }
        return noteText || "";
    }, [isReportShare, noteText]);

    const goToEmailSettings = (provider) => {
        setPathWithParams("/settings/profile", [
            { key: "accountId", value: accountId },
            { key: "facilityId", value: facilityId },
            { key: "tab", value: "email" },
            { key: "provider", value: provider },
            { key: "redirectAfter", value: redirectAfter },
        ]);
    };

    return (
        <div>
            <Dialog open={true} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle>
                    Notes share with users
                    {!!noteText && (
                        <Box mt={1} sx={{ maxWidth: noteMaxWidth, overflow: "hidden" }}>
                            <Tooltip
                                title={showTooltip ? noteText : ""}
                                arrow
                                disableHoverListener={!showTooltip}
                            >
                                <Typography
                                    noWrap
                                    variant="body2"
                                    sx={{
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                        ...(showTooltip && { cursor: "pointer" }),
                                    }}
                                >
                                    {noteText}
                                </Typography>
                            </Tooltip>
                        </Box>
                    )}
                </DialogTitle>
                {!hasConnectedEmail ? (
                    <DialogContent sx={{ textAlign: "center", py: 5 }}>
                        <Typography variant="body1" gutterBottom>
                            To share a note, you need to connect your email.
                        </Typography>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => {

                                const provider = availableIntegrations.microsoft?.connected
                                    ? "microsoft"
                                    : "google";

                                setPathWithParams("/settings/profile", [
                                    { key: "accountId", value: accountId },
                                    { key: "facilityId", value: facilityId },
                                    { key: "tab", value: "email" },
                                    { key: "provider", value: provider },
                                    { key: "redirectAfter", value: redirectAfter },
                                ]);
                            }}
                        >
                            Connect Email
                        </Button>
                    </DialogContent>
                ) : (
                    <Formik
                        initialValues={{
                            selectedUsers: [],
                            sender: availableIntegrations.microsoft?.connected
                                ? "microsoft"
                                : availableIntegrations.google?.connected
                                    ? "google"
                                    : "",
                            noteId: noteId,
                            subject: defaultSubject,
                            message: defaultMessage,
                        }}
                        validationSchema={validationSchema}
                        onSubmit={handleSubmit}
                    >
                        {({ values, setFieldValue, errors, touched, handleChange }) => (
                            <Form>
                                <DialogContent>
                                    <FormControl fullWidth margin="dense">
                                        <InputLabel id="sender-label">Send as</InputLabel>
                                        <Select
                                            labelId="sender-label"
                                            name="sender"
                                            label="Send as"
                                            value={values.sender}
                                            onChange={(e) => {
                                                const v = e.target.value;
                                                if (v === "__connect-microsoft") {
                                                    // defer until after menu close
                                                    setTimeout(() => goToEmailSettings("microsoft"), 0);
                                                    return;
                                                }
                                                if (v === "__connect-google") {
                                                    setTimeout(() => goToEmailSettings("google"), 0);
                                                    return;
                                                }
                                                handleChange(e);
                                            }}
                                            error={touched.sender && !!errors.sender}
                                        >
                                            {(availableIntegrations.microsoft?.connected ||
                                                availableIntegrations.google?.connected) && (
                                                    <ListSubheader disableSticky>Connected</ListSubheader>
                                                )}
                                            {computedSenderOptions
                                                .filter(opt => availableIntegrations[opt.id]?.connected)
                                                .map(opt => (
                                                    <MenuItem key={opt.id} value={opt.id}>
                                                        <ListItemText primary={opt.label} />
                                                    </MenuItem>
                                                ))}

                                            {/* Connect actions (only if not connected) */}
                                            {(!availableIntegrations.microsoft?.connected || !availableIntegrations.google?.connected) && (availableIntegrations.microsoft?.connected || availableIntegrations.google?.connected) && (
                                                <Divider key="divider-connect" />
                                            )}
                                            {(!availableIntegrations.microsoft?.connected || !availableIntegrations.google?.connected) && (
                                                <ListSubheader key="header-connect" disableSticky>Connect a provider</ListSubheader>
                                            )}
                                            {!availableIntegrations.microsoft?.connected && (
                                                <MenuItem
                                                    key="connect-ms"
                                                    value="__connect-microsoft"
                                                    onMouseDown={(ev) => {
                                                        ev.preventDefault(); // don’t change Select value
                                                        ev.stopPropagation();
                                                        goToEmailSettings("microsoft");
                                                    }}
                                                >
                                                    <ListItemIcon>
                                                        <OpenInNewIcon fontSize="small" />
                                                    </ListItemIcon>
                                                    <ListItemText primary="Connect Microsoft 365" />
                                                </MenuItem>
                                            )}
                                            {!availableIntegrations.google?.connected && (
                                                <MenuItem
                                                    key="connect-google"
                                                    value="__connect-google"
                                                    onMouseDown={(ev) => {
                                                        ev.preventDefault();
                                                        ev.stopPropagation();
                                                        goToEmailSettings("google");
                                                    }}
                                                >
                                                    <ListItemIcon>
                                                        <OpenInNewIcon fontSize="small" />
                                                    </ListItemIcon>
                                                    <ListItemText primary="Connect Google" />
                                                </MenuItem>
                                            )}
                                        </Select>
                                    </FormControl>

                                    <Autocomplete
                                        sx={{ mt: 1.5 }}
                                        multiple
                                        disableCloseOnSelect
                                        options={uniqueUsers}
                                        getOptionLabel={(option) => option.fullName}
                                        value={values.selectedUsers}
                                        onChange={(event, newValue) =>
                                            setFieldValue("selectedUsers", newValue)
                                        }
                                        isOptionEqualToValue={(option, value) =>
                                            option._id === value._id
                                        }
                                        renderOption={(props, option, { selected }) => {
                                            // Ensure a unique key for the list item to avoid React warnings
                                            const itemKey = option._id || option.id || option.email || option.fullName || Math.random().toString();
                                            return (
                                                <li {...props} key={itemKey}>
                                                    <Checkbox
                                                        checked={selected}
                                                        style={{ marginRight: 8 }}
                                                        size="small"
                                                    />
                                                    <ListItemText
                                                        primary={option.fullName}
                                                        secondary={maskEmail(option.email)}
                                                        secondaryTypographyProps={{ variant: 'caption', sx: { display: 'inline', ml: 1, color: 'text.secondary' } }}
                                                    />
                                                </li>
                                            );
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Select Options"
                                                error={touched.selectedUsers && !!errors.selectedUsers}
                                                helperText={
                                                    touched.selectedUsers && errors.selectedUsers
                                                }
                                            />
                                        )}
                                    />

                                    <TextField
                                        sx={{ mt: 2 }}
                                        fullWidth
                                        margin="dense"
                                        name="subject"
                                        label="Subject"
                                        value={values.subject}
                                        onChange={handleChange}
                                        error={touched.subject && !!errors.subject}
                                        helperText={touched.subject && errors.subject}
                                    />

                                    <Box sx={{ mt: 2 }}>
                                        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                                            Message
                                        </Typography>
                                        <TextareaAutosize
                                            name="message"
                                            minRows={4}
                                            style={{
                                                width: "100%",
                                                padding: 8,
                                                fontFamily: "inherit",
                                                fontSize: 14,
                                                borderRadius: 4,
                                                borderColor:
                                                    touched.message && !!errors.message
                                                        ? "#d32f2f"
                                                        : "#ccc",
                                                borderWidth: 1,
                                                borderStyle: "solid",
                                                boxSizing: "border-box",
                                                resize: "vertical",
                                            }}
                                            value={values.message}
                                            onChange={handleChange}
                                        />
                                        {touched.message && errors.message && (
                                            <Typography variant="caption" color="error">
                                                {errors.message}
                                            </Typography>
                                        )}
                                    </Box>
                                </DialogContent>
                                <DialogActions>
                                    <Button onClick={handleClose}>Cancel</Button>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        color="primary"
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? "Sharing..." : "Share"}
                                    </Button>
                                </DialogActions>
                            </Form>
                        )}
                    </Formik>
                )}
            </Dialog>
        </div>
    );
};

export default ShareNotesDialog;
